import { useState, useEffect, useMemo } from "react";
import { backendURL, IS_PUBLIC, PROXY_URL } from "../Globals";
import { Table, Input, Button, Alert, Tooltip } from 'antd';
import { SearchOutlined, ArrowUpOutlined, LockOutlined } from '@ant-design/icons';
import Select from 'react-select';
import ReactECharts from 'echarts-for-react';

const Q_COLORS = { Q1: '#27ae60', Q2: '#2980b9', Q3: '#f39c12', Q4: '#e74c3c', 'Sin JCR': '#ccc', 'Sin SJR': '#ccc' };
const Q_BADGE_COLOR = { Q1: '#27ae60', Q2: '#f1c40f', Q3: '#e67e22', Q4: '#e74c3c' };
const Q_BADGE_TEXT  = { Q1: '#fff',    Q2: '#7a5a00', Q3: '#fff',    Q4: '#fff'    };

const Highlight = ({ text, query }) => {
  const escHtml = s => String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  if (!query || !text) return <span dangerouslySetInnerHTML={{ __html: escHtml(text) }} />;
  const esc = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = String(text).split(new RegExp(`(${esc})`, 'gi'));
  const html = parts.map((p, i) =>
    i % 2 === 1
      ? `<span style="background-color:#e8b400">${escHtml(p)}</span>`
      : escHtml(p)
  ).join('');
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
};

const PUB_COLORS = ['#5470c6','#91cc75','#fac858','#ee6666','#73c0de','#3ba272','#fc8452','#9a60b4'];

const fmtN = n => String(Math.round(Number(n))).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

const toTitleCase = str => str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());

const formatJcrCats = str => {
  if (!str) return '';
  return str.split(/[;,]/).map(c => toTitleCase(c.trim())).filter(Boolean).join(', ');
};

const parseSjrCats = str => {
  if (!str) return [];
  return str.split(';').map(c => {
    const trimmed = c.trim();
    const qMatch = trimmed.match(/\(Q([1-4])\)\s*$/);
    const q = qMatch ? `Q${qMatch[1]}` : null;
    const name = toTitleCase(trimmed.replace(/\s*\(Q[1-4]\)\s*$/, '').trim());
    return { name, q };
  }).filter(c => c.name);
};

const formatSjrCats = str => parseSjrCats(str).map(c => c.name).join(', ');

const getTopSjrCats = (str, q) => {
  if (!str || !q) return null;
  const matching = parseSjrCats(str).filter(c => c.q === q).map(c => c.name);
  return matching.length ? matching.join(', ') : null;
};

const makePieOption = (data, useQColors) => ({
  tooltip: { trigger: 'item', formatter: p => `${p.name}: <b>${fmtN(p.value)}</b> (${p.percent.toFixed(1)}%)` },
  series: [{
    type: 'pie', radius: ['48%', '76%'],
    data: data.map((d, i) => ({
      name: d.name, value: d.value,
      itemStyle: { color: useQColors ? (Q_COLORS[d.name] || '#aaa') : PUB_COLORS[i % PUB_COLORS.length] }
    })),
    label: { show: false },
    emphasis: { label: { show: true, fontSize: 10, fontWeight: 'bold' } },
    animationType: 'scale', animationEasing: 'elasticOut',
  }]
});

const quarters = [
  { value: 'Q1', label: 'Q1' },
  { value: 'Q2', label: 'Q2' },
  { value: 'Q3', label: 'Q3' },
  { value: 'Q4', label: 'Q4' },
];

const SEL = {
  placeholder: p => ({ ...p, color: '#aaa', fontSize: '13px' }),
  control: (p, s) => ({
    ...p, minHeight: '36px', fontSize: '13px',
    borderColor: s.isFocused ? '#007398' : '#d9d9d9',
    boxShadow: s.isFocused ? '0 0 0 2px rgba(0,115,152,0.15)' : 'none',
    borderRadius: '6px',
    '&:hover': { borderColor: '#007398' },
  }),
  option: (p, s) => ({
    ...p, textAlign: 'left', fontSize: '13px',
    backgroundColor: s.isSelected ? '#007398' : s.isFocused ? '#e8f4f7' : 'white',
    color: s.isSelected ? 'white' : '#333',
  }),
  valueContainer: p => ({ ...p, padding: '0 8px' }),
  singleValue: p => ({ ...p, fontSize: '13px', color: '#333' }),
  menu: p => ({ ...p, zIndex: 9999, borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }),
  menuPortal: p => ({ ...p, zIndex: 9999 }),
};

const SearchComponent = () => {
  const [issn, setIssn] = useState('');
  const [jcrq, setJcrq] = useState(null);
  const [sjrq, setSjrq] = useState(null);
  const [publisher, setPublisher] = useState(null);
  const [jcrCategory, setJcrCategory] = useState(null);
  const [sjrCategory, setSjrCategory] = useState(null);
  const [editors, setEditors] = useState([]);
  const [jcrCatOpts, setJcrCatOpts] = useState([]);
  const [sjrCatOpts, setSjrCatOpts] = useState([]);
  const [allJournals, setAllJournals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filtrado 100% client-side: reacciona automáticamente a cualquier cambio de filtro
  const visibleJournals = useMemo(() => {
    const q = issn.trim().toLowerCase();
    return allJournals.filter(j => {
      if (q && !(
        (j.title    || '').toLowerCase().includes(q) ||
        (j.issn     || '').toLowerCase().includes(q) ||
        (j.publisher|| '').toLowerCase().includes(q)
      )) return false;
      if (publisher    && j.publisher !== publisher)                        return false;
      if (jcrq         && j.jcrq !== jcrq)                                  return false;
      if (sjrq         && j.sjrq !== sjrq)                                  return false;
      if (jcrCategory  && !(j.jcr_categories || '').includes(jcrCategory)) return false;
      if (sjrCategory  && !(j.sjr_categories  || '').includes(sjrCategory)) return false;
      return true;
    });
  }, [allJournals, issn, publisher, jcrq, sjrq, jcrCategory, sjrCategory]);

  useEffect(() => {
    const pp = !IS_PUBLIC ? '?_p=1' : '';
    Promise.all([
      fetch(`${backendURL}/journals/stats`).then(r => r.json()),
      fetch(`${backendURL}/journals/publishers`).then(r => r.json()),
      fetch(`${backendURL}/journals/jcr-categories`).then(r => r.json()),
      fetch(`${backendURL}/journals/sjr-categories`).then(r => r.json()),
      fetch(`${backendURL}/journals${pp}`).then(r => r.json()),
    ]).then(([statsData, pubData, jcrCats, sjrCats, data]) => {
      setStats(statsData);
      setEditors(pubData);
      setJcrCatOpts(jcrCats.map(c => ({ value: c.value, label: toTitleCase(c.value) })));
      setSjrCatOpts(sjrCats);
      setAllJournals([...data].sort((a, b) => (a.title || '').localeCompare(b.title || '', 'es')));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Registra la búsqueda para analítica (fire-and-forget); los resultados ya son client-side
  const clickSearch = () => {
    const queryStr = [issn, publisher, jcrq, sjrq, jcrCategory, sjrCategory].filter(Boolean).join(' | ');
    if (queryStr) {
      fetch(`${backendURL}/usage/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ module: 'transformativos', query: queryStr }),
      }).catch(() => {});
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') { e.preventDefault(); clickSearch(); }
  };

  const columns = [
    {
      title: "Revista",
      dataIndex: "title",
      render: (text, record) => (
        <div className="jcard-main">
          <div className="jcard-title">
            {record.url
              ? <a href={record.url.startsWith('http') ? record.url : 'https://' + record.url}
                   target="_blank" rel="noopener noreferrer">
                  <Highlight text={text} query={issn} /><ArrowUpOutlined className="jcard-link-icon" />
                </a>
              : <span><Highlight text={text} query={issn} /></span>}
          </div>
          {!IS_PUBLIC && record.jcr_categories && (
            <div className="jcard-cats">
              <span className="jcard-cat-label">{'Categor\u00EDa JCR:'}</span>
              <span className="jcard-cat-val">{formatJcrCats(record.jcr_categories)}</span>
            </div>
          )}
          {!IS_PUBLIC && record.sjr_categories && (
            <div className="jcard-cats">
              <span className="jcard-cat-label">{'Categor\u00EDa SJR:'}</span>
              <span className="jcard-cat-val">{formatSjrCats(record.sjr_categories)}</span>
            </div>
          )}
        </div>
      )
    },
    {
      title: "Editorial",
      dataIndex: "publisher",
      width: 200,
      render: (text, record) => (
        <div className="jcard-pub-section">
          <div className="jcard-pub-name"><Highlight text={text} query={issn} /></div>
          {!IS_PUBLIC && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
            <div style={{ fontSize: 11, lineHeight: 1.9, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: '#aaa', minWidth: 70 }}>Cuartil JCR:</span>
              {record.jcrq
                ? <Tooltip
                    mouseEnterDelay={0.2}
                    title={<span>{formatJcrCats(record.jcr_categories)}{record.jcr_year ? <><br /><span style={{ color: '#aaa', fontSize: 11 }}>Año: {record.jcr_year}</span></> : null}</span>}
                  >
                    <span style={{ background: Q_BADGE_COLOR[record.jcrq], color: Q_BADGE_TEXT[record.jcrq], fontWeight: 700, padding: '1px 7px', borderRadius: 3, fontSize: 11, cursor: 'default' }}>{record.jcrq}</span>
                  </Tooltip>
                : <span style={{ color: '#bbb', fontSize: 11 }}>No indexada</span>}
            </div>
            <div style={{ fontSize: 11, lineHeight: 1.9, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: '#aaa', minWidth: 70 }}>Cuartil SJR:</span>
              {record.sjrq
                ? <Tooltip
                    mouseEnterDelay={0.2}
                    title={<span>{getTopSjrCats(record.sjr_categories, record.sjrq)}{record.sjr_year ? <><br /><span style={{ color: '#aaa', fontSize: 11 }}>Año: {record.sjr_year}</span></> : null}</span>}
                  >
                    <span style={{ background: Q_BADGE_COLOR[record.sjrq], color: Q_BADGE_TEXT[record.sjrq], fontWeight: 700, padding: '1px 7px', borderRadius: 3, fontSize: 11, cursor: 'default' }}>{record.sjrq}</span>
                  </Tooltip>
                : <span style={{ color: '#bbb', fontSize: 11 }}>No indexada</span>}
            </div>
          </div>
          )}
        </div>
      )
    },
    {
      title: "APC",
      dataIndex: "apc coverage",
      width: 130,
      render: (value, record) => {
        const fmt = value === '1' ? '100%' : value === '0,1' ? '10%' : value === '0.1' ? '10%' : value === '0,2' ? '20%' : value === '0.2' ? '20%' : (value || '--');
        const full = fmt === '100%';
        const hasDiscount = fmt !== '--';
        return (
          <div className="jcard-apc-section">
            <Tooltip title="e-ISSN" mouseEnterDelay={0.3}>
              <span style={{ fontSize: 15, fontWeight: 600, color: '#007398', cursor: 'default' }}>
                <Highlight text={record.issn} query={issn} />
              </span>
            </Tooltip>
            {hasDiscount && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, marginTop: 6 }}>
                <div className={`jcard-apc-label${full ? '' : ' jcard-apc-label-partial'}`}>Descuento</div>
                <div className={`jcard-apc-pct ${full ? 'jcard-apc-full' : 'jcard-apc-partial'}`}>{fmt}</div>
              </div>
            )}
          </div>
        );
      }
    },
  ];



  return (
    <div className="search-page">

      {/* Info header */}
      <div style={{ paddingTop: 28, paddingBottom: 4, textAlign: 'center' }}>
        <div style={{
          fontSize: 22, fontWeight: 700, color: '#1a1a1a',
          letterSpacing: -0.3, lineHeight: 1.3,
          fontFamily: "'Inter','Segoe UI',sans-serif",
          padding: '0 24px',
        }}>
          Revistas de acceso abierto con cargos de publicación (APC) cubiertos
        </div>
      </div>

      {/* Banner modo público: solo visible cuando REACT_APP_MODE=public */}
      {IS_PUBLIC && (
        <div style={{
          background: 'linear-gradient(90deg, #007398 0%, #005a7a 100%)',
          color: '#fff',
          padding: '14px 32px',
          textAlign: 'center',
          fontSize: 15,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}>
          <LockOutlined style={{ fontSize: 18 }} />
          <span>
            Para ver <strong>cuartiles JCR/SJR y categor&#237;as</strong>, accede con tus credenciales UniOvi:
          </span>
          <a
            href={PROXY_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#ffd700', fontWeight: 700, textDecoration: 'underline', whiteSpace: 'nowrap' }}
          >
            Acceso con credenciales UniOvi &#8594;
          </a>
        </div>
      )}

      {/* Insights */}
      {stats && (
        <div className="insights-row">
          <div className="insights-stat-block">
            <div className="insights-number">{fmtN(stats.total)}</div>
            <div className="insights-label">Revistas elegibles</div>
          </div>
          {!IS_PUBLIC && (<>
          <div className="insights-chart-card">
            <div className="insights-chart-title">Cuartil JCR</div>
            <ReactECharts option={makePieOption(stats.byJcrq.map(d => ({ ...d, name: d.name || 'Sin JCR' })), true)} style={{ height: '110px', width: '150px' }} opts={{ renderer: 'svg' }} />
          </div>
          <div className="insights-chart-card">
            <div className="insights-chart-title">Cuartil SJR</div>
            <ReactECharts option={makePieOption(stats.bySjrq.map(d => ({ ...d, name: d.name || 'Sin SJR' })), true)} style={{ height: '110px', width: '150px' }} opts={{ renderer: 'svg' }} />
          </div>
          </>)}
          <div className="insights-chart-card">
            <div className="insights-chart-title">Por editorial</div>
            <ReactECharts option={makePieOption(stats.byPublisher, false)} style={{ height: '110px', width: '150px' }} opts={{ renderer: 'svg' }} />
          </div>
        </div>
      )}

      {/* Hero + search card */}
      <div className="search-hero">
        <div className="search-modern-card">
          <div className="search-main-row">
            <Input
              size="large"
              placeholder={'Buscar por t\u00EDtulo de revista o eISSN\u2026'}
              value={issn}
              onChange={e => setIssn(e.target.value)}
              onKeyDown={handleKeyDown}
              prefix={<SearchOutlined style={{ color: '#aaa', fontSize: '18px' }} />}
              className="search-main-input"
            />
            <Button type="primary" size="large" onClick={clickSearch}
              icon={<SearchOutlined />} className="search-main-btn">
              Buscar
            </Button>
          </div>
          <div className="search-filters-grid">
            <div className="search-filter-item">
              <label className="search-filter-label">Editorial</label>
              <Select
                options={editors}
                value={editors.find(e => e.value === publisher) || null}
                onChange={o => setPublisher(o ? o.value : null)}
                placeholder={'Todas las editoriales'}
                isClearable isSearchable styles={SEL}
                menuPortalTarget={document.body}
              />
            </div>
            {!IS_PUBLIC && (<>
            <div className="search-filter-item">
              <label className="search-filter-label">Cuartil JCR</label>
              <Select
                options={quarters}
                value={quarters.find(q => q.value === jcrq) || null}
                onChange={o => setJcrq(o ? o.value : null)}
                placeholder={'Cualquier cuartil'}
                isClearable styles={SEL}
                menuPortalTarget={document.body}
              />
            </div>
            <div className="search-filter-item">
              <label className="search-filter-label">Cuartil SJR</label>
              <Select
                options={quarters}
                value={quarters.find(q => q.value === sjrq) || null}
                onChange={o => setSjrq(o ? o.value : null)}
                placeholder={'Cualquier cuartil'}
                isClearable styles={SEL}
                menuPortalTarget={document.body}
              />
            </div>
            <div className="search-filter-item">
              <label className="search-filter-label">{'Categor\u00EDa JCR (WoS)'}</label>
              <Select
                options={jcrCatOpts}
                value={jcrCatOpts.find(c => c.value === jcrCategory) || null}
                onChange={o => setJcrCategory(o ? o.value : null)}
                placeholder={'Todas las categor\u00EDas'}
                isClearable isSearchable styles={SEL}
                menuPortalTarget={document.body}
              />
            </div>
            <div className="search-filter-item">
              <label className="search-filter-label">{'Categor\u00EDa SJR (Scopus)'}</label>
              <Select
                options={sjrCatOpts}
                value={sjrCatOpts.find(c => c.value === sjrCategory) || null}
                onChange={o => setSjrCategory(o ? o.value : null)}
                placeholder={'Todas las categor\u00EDas'}
                isClearable isSearchable styles={SEL}
                menuPortalTarget={document.body}
              />
            </div>
            </>)}
          </div>
        </div>
      </div>

      {/* Sin resultados */}
      {!loading && allJournals.length > 0 && visibleJournals.length === 0 && (
        <Alert
          message="No se encontraron revistas con los filtros actuales"
          type="warning" showIcon
          style={{ width: '50%', margin: '20px auto' }}
        />
      )}

      {/* Results */}
      <div className="journals-table-wrapper">
        {visibleJournals.length > 0 && (
          <div className="journals-result-bar">
            <span className="journals-result-count">{fmtN(visibleJournals.length)} revistas encontradas</span>
          </div>
        )}
        <Table
          columns={columns}
          dataSource={visibleJournals}
          rowKey="issn"
          className="custom-table journals-table"
          pagination={{ pageSize: 20, showSizeChanger: false }}
          loading={loading}
          locale={{ emptyText: loading ? ' ' : 'No se encontraron revistas' }}
        />
      </div>
    </div>
  );
};

export default SearchComponent;
